// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
import { ActivityDetailModal, BaseTimeLine } from '~/components/module/activity';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { BusinessTypeEnum, LoadingSizeEnum } from '~/utils/enum/common';
import { IActivity, IQueryParamActivity } from '~/utils/interface/activity';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { getActivityList } from '~/thunks/activity/activityThunk';
// Styles, images, icons
import styles from './ActivityLogProductTab.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ActivityLogProductTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const { id: inventoryId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [activityList, setActivityList] = useState<IActivity[]>([]);
  const [currentPageActivity, setCurrentPageActivity] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [activitySelected, setActivitySelected] = useState<IActivity | null>(null);
  const [isShowActivityDetail, setIsShowActivityDetail] = useState<boolean>(false);
  const [hasMoreActivity, setHasMoreActivity] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!inventoryId) return;

    const params: IQueryParamActivity = {
      page: currentPageActivity,
      limit: DEFAULT_LIMIT_PAGE,
      moduleId: inventoryId,
      moduleType: BusinessTypeEnum.INVENTORY,
    };

    handleGetActivityList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageActivity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleLoadMore = () => {
    setCurrentPageActivity((prev) => prev + 1);
  };

  const handleViewDetail = (activity: IActivity) => {
    setIsShowActivityDetail(true);
    setActivitySelected(activity);
  };

  const handleCloseActivityDetail = () => {
    setIsShowActivityDetail(false);
    setActivitySelected(null);
  };

  const handleGetActivityList = (params: IQueryParamActivity) => {
    if (currentPageActivity === DEFAULT_CURRENT_PAGE) {
      setIsLoading(true);
    }

    dispatch(getActivityList(params))
      .unwrap()
      .then((res) => {
        if (!res?.data?.responses) return;

        const { responses } = res?.data;

        if (!responses || responses?.length === 0) {
          setHasMoreActivity(false);

          return;
        }

        setActivityList((prev) => {
          const newData = responses?.filter((data) => !prev?.some((oldActivity) => oldActivity?.id === data?.id));

          const updateList = [...prev, ...newData];

          setHasMoreActivity(true);

          return updateList;
        });
      })
      .catch((_error) => {
        setHasMoreActivity(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box id='activityLogProductTabComponent' className={cx('container')}>
      {!isLoading &&
        (Array.isArray(activityList) && activityList?.length > 0 ? (
          <BaseTimeLine
            data={activityList}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreActivity}
            onView={handleViewDetail}
            loadMoreSize={LoadingSizeEnum.MEDIUM}
          />
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available')}
          </Typography>
        ))}

      <ActivityDetailModal
        isOpen={isShowActivityDetail}
        {...(activitySelected && { activity: activitySelected })}
        onClose={handleCloseActivityDetail}
      />

      {isLoading && <Loading />}
    </Box>
  );
};

export default ActivityLogProductTab;
